import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Crown, Volume2 } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";

/**
 * Renders when an audio commercial is playing (free tier only). The ad
 * itself is spoken via SpeechSynthesis from PlayerContext; this just shows
 * the "Ad — Upgrade to skip" banner + CTA above the BottomPlayer.
 *
 * Premium / Artist subscribers (incl. their 3-month trial) never see this
 * because /api/ads/config returns ads_enabled=false for them.
 */
const AdBanner = () => {
  const { currentAd, skipAd } = usePlayer();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!currentAd) { setElapsed(0); return; }
    const t = setInterval(() => setElapsed((e) => e + 100), 100);
    return () => clearInterval(t);
  }, [currentAd]);

  if (!currentAd?.ad) return null;
  const ad = currentAd.ad;
  const total = ad.duration_estimate_ms || 12000;
  const pct = Math.min(100, (elapsed / total) * 100);

  return (
    <div
      data-testid="ad-banner"
      className="md:hidden-when-empty fixed left-0 right-0 z-50 bottom-[176px] md:bottom-[96px] mx-auto px-3 pointer-events-none"
    >
      <div className="max-w-3xl mx-auto pointer-events-auto bg-gradient-to-r from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] p-[1.5px] rounded-2xl shadow-2xl">
        <div className="bg-[#0c0c0d] rounded-[14px] px-4 py-3 flex items-center gap-4">
          {/* Pulsing speaker icon */}
          <div className="relative shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2E00] to-[#FF2E00] flex items-center justify-center">
            <Volume2 size={18} className="text-white" />
            <span className="absolute inset-0 rounded-full ring-2 ring-[#FF2E00] animate-ping opacity-50"></span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-wider font-bold bg-[#FF2E00]/20 text-[#FF99B3] px-2 py-0.5 rounded-full flex items-center gap-1">
                <Megaphone size={10} /> Ad
              </span>
              <span className="text-[10px] text-white/45">Upgrade for ad-free listening</span>
            </div>
            <p
              data-testid="ad-banner-message"
              className="text-sm text-white/85 truncate leading-tight"
              title={ad.message}
            >
              {ad.message}
            </p>
            {/* Progress bar */}
            <div className="mt-1.5 h-[3px] rounded-full bg-white/8 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF2E00] to-[#E2FF31] transition-[width] duration-100 ease-linear"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          <div className="shrink-0">
            <Link
              data-testid="ad-banner-upgrade-btn"
              to={ad.cta?.href || "/premium"}
              onClick={() => skipAd({ ctaClicked: true })}
              className="bg-[#E2FF31] hover:bg-[#d4f01a] text-black font-semibold rounded-full px-3.5 py-2 text-[12px] flex items-center gap-1.5 transition-transform active:scale-95 whitespace-nowrap"
            >
              <Crown size={11} /> {ad.cta?.label || "Upgrade"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdBanner;
